import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  api,
  clearTokens,
  setUnauthenticatedHandler,
  storeSession,
  type SignInInput,
  type SignUpInput,
} from "../api/client";
import type { CurrentUser } from "../api/types";

interface AuthState {
  user: CurrentUser | null;
  /** True until the stored refresh token has been tried. Avoids a sign-in flash. */
  initialising: boolean;
  signIn: (input: SignInInput) => Promise<void>;
  signUp: (input: SignUpInput) => Promise<void>;
  acceptInvite: (input: { token: string; password: string }) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [initialising, setInitialising] = useState(true);

  // Resume on load so a reload does not force a sign-in.
  useEffect(() => {
    let cancelled = false;
    void api.resumeSession().then((resumed) => {
      if (cancelled) return;
      setUser(resumed);
      setInitialising(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // A 401 that refresh could not fix means the session is genuinely over. The
  // client calls this so the UI reacts without every screen handling 401 itself.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setUser(null);
    });
    return () => setUnauthenticatedHandler(null);
  }, []);

  const signIn = useCallback(async (input: SignInInput) => {
    const session = await api.signIn(input);
    storeSession(session.tokens);
    setUser(session.user);
  }, []);

  const signUp = useCallback(async (input: SignUpInput) => {
    const session = await api.signUp(input);
    storeSession(session.tokens);
    setUser(session.user);
  }, []);

  const acceptInvite = useCallback(async (input: { token: string; password: string }) => {
    const session = await api.acceptInvite(input);
    storeSession(session.tokens);
    setUser(session.user);
  }, []);

  const signOut = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, initialising, signIn, signUp, acceptInvite, signOut }),
    [user, initialising, signIn, signUp, acceptInvite, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside <AuthProvider>.");
  return context;
}